import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Globe, Award, ArrowRight } from "lucide-react";
import { useSEO } from "../lib/useSEO";

const FAQ = [
    {
        q: "How do I know I'm on the official Carbon Labs Australia website?",
        a: "The official Carbon Labs Australia website is www.carbonlabsau.com. Verify the domain in your browser address bar — it should read exactly that, over HTTPS with a valid TLS certificate.",
    },
    {
        q: "Where can I verify I'm ordering from Carbon Labs Australia?",
        a: "Every genuine order confirmation is sent from carbonlabsau@outlook.com and references the official domain www.carbonlabsau.com. Our verified socials (@carbonlabsau on TikTok, Carbon Labs AU on Facebook) also link only to this domain.",
    },
    {
        q: "What is the official domain?",
        a: "www.carbonlabsau.com. This is the only domain Carbon Labs Australia operates from for online orders and customer communications.",
    },
    {
        q: "Are your products for research use only?",
        a: "Yes. All products supplied by Carbon Labs Australia are strictly for research and laboratory purposes and are not for human consumption.",
    },
    {
        q: "Do you ship to New Zealand as well as Australia?",
        a: "Yes — we dispatch to both Australia (same-day cut-off in Sydney) and New Zealand with tracked shipping.",
    },
];

export default function OfficialSitePage() {
    useSEO({
        title: "Official Carbon Labs Australia | www.carbonlabsau.com",
        description:
            "The official Carbon Labs Australia website — verify you're on www.carbonlabsau.com, the only official domain for genuine Carbon Labs AU research products.",
        canonical: "https://www.carbonlabsau.com/official",
        keywords:
            "Carbon Labs Australia, Carbon Labs AU, official website, carbonlabsau.com, Australian research supplier",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    });

    return (
        <main className="container-x py-16 sm:py-20" data-testid="official-page">
            <header className="max-w-3xl mb-14">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--tiffany)] mb-3">// Verified Source</p>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter mb-5">
                    Official <span className="text-gold-gradient">Carbon Labs Australia</span>
                </h1>
                <p className="text-base sm:text-lg text-[var(--text-secondary)] leading-relaxed">
                    You are on the official Carbon Labs Australia website. Our only online domain
                    is <strong className="text-white">www.carbonlabsau.com</strong> — every genuine
                    product, order confirmation and support reply originates from here.
                </p>
            </header>

            <section className="grid md:grid-cols-3 gap-4 mb-14" aria-label="Verification badges">
                {[
                    { icon: Globe, label: "Official domain", value: "www.carbonlabsau.com" },
                    { icon: ShieldCheck, label: "Secure checkout", value: "HTTPS + Stripe" },
                    { icon: Award, label: "Australian supplier", value: "Sydney dispatch" },
                ].map((c, i) => {
                    const Icon = c.icon;
                    return (
                        <div key={i} className="rounded-2xl p-5 glass border border-white/5" data-testid={`official-badge-${i}`}>
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 bg-[rgba(233,188,11,0.08)] border border-[rgba(233,188,11,0.2)]">
                                <Icon size={16} className="text-[var(--gold)]" />
                            </div>
                            <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{c.label}</p>
                            <p className="text-sm font-semibold text-white mt-1">{c.value}</p>
                        </div>
                    );
                })}
            </section>

            <section className="max-w-3xl">
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-6">Frequently Asked</h2>
                <div className="space-y-3">
                    {FAQ.map((f, i) => (
                        <details
                            key={i}
                            className="rounded-2xl glass border border-white/5 overflow-hidden group"
                            data-testid={`official-faq-${i}`}
                        >
                            <summary className="p-5 cursor-pointer flex items-center justify-between gap-4 text-sm md:text-base font-display font-semibold text-white group-open:text-[var(--gold)]">
                                {f.q}
                                <ArrowRight size={14} className="shrink-0 transition-transform group-open:rotate-90 text-[var(--gold)]" />
                            </summary>
                            <p className="px-5 pb-5 text-sm text-[var(--text-secondary)] leading-relaxed">{f.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            <div className="mt-12">
                <Link to="/shop" className="btn-solid" data-testid="official-shop-cta">
                    Browse the official catalogue <ArrowRight size={14} />
                </Link>
            </div>
        </main>
    );
}
